import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CdkDragMove,
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { normalizeFlamePickerModelId } from 'src/app/shared/clari-chat-model-catalog';

export interface ModelMeta {
  id: string;
  name: string;
}

export interface DndData {
  selected: string[];
  available: ModelMeta[];
}

@Component({
  selector: 'app-team-ai-drag-drop-dialog',
  templateUrl: './team-ai-drag-drop-dialog.component.html',
  styleUrls: ['./team-ai-drag-drop-dialog.component.scss']
})
export class TeamAiDragDropDialogComponent {
  private readonly tabCount = 4;
  private readonly blockedModelIds = new Set([
    'commandlight',
    'pegasus',
    'qwencoder',
    'qwen-coder',
    'qwencoder'
  ]);
  @ViewChild('dialogContainer', { static: true })
  dialogContainer!: ElementRef<HTMLDivElement>;

  onDragMoved(event: CdkDragMove) {
    if (!this.dialogContainer) return;

    const container = this.dialogContainer.nativeElement;
    const { y } = event.pointerPosition;
    const rect = container.getBoundingClientRect();

    const edgeSize = 60;
    const scrollSpeed = 15;

    if (y < rect.top + edgeSize) {
      container.scrollTop -= scrollSpeed;
    } else if (y > rect.bottom - edgeSize) {
      container.scrollTop += scrollSpeed;
    }
  }

  availableModels: ModelMeta[] = [];

  tab1List: ModelMeta[] = [];
  tab2List: ModelMeta[] = [];
  tab3List: ModelMeta[] = [];
  tab4List: ModelMeta[] = [];

  private tabLists(): ModelMeta[][] {
    return [this.tab1List, this.tab2List, this.tab3List, this.tab4List];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DndData,
    public dialogRef: MatDialogRef<TeamAiDragDropDialogComponent>
  ) {
    const normalizedSelected = (data.selected || []).map((id) => this.normalizeModelId(id));
    const selectedSet = new Set(normalizedSelected);

    const byId = new Map<string, ModelMeta>();
    (data.available || [])
      .filter((m) => !this.isBlockedModelId(m.id))
      .forEach((m) => {
      const id = this.normalizeModelId(m.id);
      byId.set(id, { ...m, id });
      });
    const sortedAvailable = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));

    const tabs = this.tabLists();
    for (let i = 0; i < tabs.length; i++) {
      const selId = normalizedSelected[i];
      if (!selId || !byId.has(selId)) {
        continue;
      }

      const selectedModel = byId.get(selId);
      if (selectedModel && !this.isModelAssignedToAnyTab(selectedModel.id)) {
        tabs[i].push(selectedModel);
      }
    }

    for (let i = 0; i < this.tabCount; i++) {
      if (tabs[i]?.length > 0) {
        continue;
      }

      const fallback = sortedAvailable.find((m) => !this.isModelAssignedToAnyTab(m.id));
      if (fallback) {
        tabs[i].push(fallback);
      }
    }

    this.availableModels = (data.available || [])
      .filter((m) => !this.isBlockedModelId(m.id))
      .filter((m) => !selectedSet.has(this.normalizeModelId(m.id)))
      .map((m) => ({ ...m, id: this.normalizeModelId(m.id) }));
  }

  drop(event: CdkDragDrop<ModelMeta[]>) {
    if (event.previousContainer === event.container) {
      if (event.container.id === 'availableList') {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      }
      return;
    }

    const sourceId = event.previousContainer.id;
    const targetId = event.container.id;

    const source = event.previousContainer.data;
    const target = event.container.data;

    if (sourceId === 'availableList' && targetId.startsWith('tab')) {
      if (target.length > 0) {
        this.availableModels.push(target[0]);
        target.splice(0, 1);
      }
      transferArrayItem(source, target, event.previousIndex, 0);
      this.reconcileTabAssignments();
      return;
    }

    if (targetId === 'availableList' && sourceId.startsWith('tab')) {
      transferArrayItem(source, target, event.previousIndex, target.length);
      this.reconcileTabAssignments();
      return;
    }

    if (sourceId.startsWith('tab') && targetId.startsWith('tab')) {
      const sourceModel = source[event.previousIndex];
      if (!sourceModel) {
        return;
      }

      if (target.length === 0) {
        transferArrayItem(source, target, event.previousIndex, 0);
      } else {
        const targetModel = target[0];
        target[0] = sourceModel;
        source[event.previousIndex] = targetModel;
      }

      this.reconcileTabAssignments();
      return;
    }

    transferArrayItem(source, target, event.previousIndex, event.currentIndex);
    this.reconcileTabAssignments();
  }

  apply(): void {
    this.reconcileTabAssignments();

    const chosenIds = this.tabLists().map((list) => this.normalizeModelId(list[0]?.id || null) || null);

    if (chosenIds.some((id) => !id)) {
      alert('Please fill all four tabs with a model.');
      return;
    }

    const uniq = new Set(chosenIds as string[]);
    if (uniq.size < 4) {
      alert('Please choose four distinct models.');
      return;
    }

    this.dialogRef.close(chosenIds as string[]);
  }

  private reconcileTabAssignments(): void {
    const lists = this.tabLists();
    const seen = new Set<string>();
    const duplicates: Array<{ listIndex: number; model: ModelMeta | undefined }> = [];

    lists.forEach((list, listIndex) => {
      const model = list[0];
      if (!model) return;

      const normalizedId = this.normalizeModelId(model.id);
      if (!normalizedId) return;

      if (seen.has(normalizedId)) {
        duplicates.push({ listIndex, model });
        return;
      }

      seen.add(normalizedId);
    });

    if (duplicates.length === 0) {
      return;
    }

    duplicates.forEach(({ listIndex, model }) => {
      const list = lists[listIndex];
      if (!list || !model) return;

      if (!this.availableModels.some((m) => this.normalizeModelId(m.id) === this.normalizeModelId(model.id))) {
        this.availableModels.unshift({ ...model, id: this.normalizeModelId(model.id) });
      }

      list.splice(0, list.length);
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private isModelAssignedToAnyTab(modelId: string): boolean {
    return this.tabLists().some((list) => list.some((model) => model.id === modelId));
  }

  private normalizeModelId(value?: string | null): string {
    return normalizeFlamePickerModelId(value);
  }

  private isBlockedModelId(value?: string | null): boolean {
    const normalizedId = this.normalizeModelId(value);
    return this.blockedModelIds.has(normalizedId);
  }
}
